package link.erpango.quip.domain.ocr

/**
 * Result of an on-device OCR pass.
 *
 * Failures are reported through [error] rather than thrown exceptions, so callers can handle
 * every outcome exhaustively without try/catch.
 */
data class OcrResult(
    val text: String,
    val blocks: List<OcrBlock> = emptyList(),
    val language: String? = null,
    val error: OcrError? = null,
) {
    val isSuccess: Boolean get() = error == null

    companion object {
        fun failure(error: OcrError): OcrResult = OcrResult(text = "", error = error)
    }
}

/**
 * Coarse structural grouping of the recognized text. [lines] keeps the raw line order inside
 * the block so form-oriented parsers can reason about layout without depending on ML Kit types.
 */
data class OcrBlock(
    val text: String,
    val lines: List<String> = emptyList(),
)

/**
 * Typed OCR failures. Deliberately not an exception hierarchy — recognition is expected to fail
 * on common conditions (unsupported target, unreadable image) and callers treat that as data.
 */
sealed class OcrError {
    data object UnsupportedPlatform : OcrError()
    data object PermissionDenied : OcrError()
    data object DecodeFailed : OcrError()
    data object ImageTooLarge : OcrError()
    data class RecognitionFailed(val message: String?) : OcrError()
}
